const STATUS_ALIASES = Object.freeze({
  arrived: 'arrived_at_port',
  available: 'available_for_pickup',
  available_pickup: 'available_for_pickup',
  delivered: 'loaded_delivered',
  delivered_to_warehouse: 'delivered_to_warehouse',
  empty_returned_to_terminal: 'empty_returned_to_terminal',
});

/**
 * Every Container status reads in Chinese, in both locales (Alan, 2026-08-08).
 *
 * This file used to hold an English label and a Chinese one, and four imported
 * Ocean Statuses had English in both slots with a note that translating them was
 * a product decision. That decision is made: the operation runs in Chinese, the
 * workbook these values were imported from is written in Chinese - 8未到港,
 * 4可提, 2已报空, 7查验 - and the staff reading the table think in those words.
 * Two characters also cost a third of the column that "Not Arrived at Port" did,
 * which is the other half of why Alan asked.
 *
 * Both slots carry the same string on purpose. The status vocabulary is not
 * translated chrome; it is the name of the thing, the same way a container
 * number is. Switching the interface to English must not rename the statuses out
 * from under the person reading them.
 */
const STATUS_PRESENTATION = Object.freeze({
  pending: ['pending', 'pending', 'danger'],
  past_due: ['已逾期', '已逾期', 'danger'],
  arrived_at_port: ['已到港', '已到港', 'danger'],
  active: ['进行中', '进行中', 'active'],
  dispatched: ['已派车', '已派车', 'active'],
  in_progress: ['运输中', '运输中', 'active'],
  out_for_delivery: ['正在送达', '正在送达', 'active'],
  available_for_pickup: ['可提', '可提', 'transit'],
  picked_up_from_terminal: ['已提柜', '已提柜', 'transit'],
  arrived_at_warehouse: ['已到仓', '已到仓', 'transit'],
  loaded_delivered: ['重柜已送达', '重柜已送达', 'handoff'],
  delivered_to_warehouse: ['已送仓', '已送仓', 'handoff'],
  // 转仓 - moved between warehouses. Added 2026-08-08: it has left the first
  // warehouse and has not been devanned at the second, which is neither
  // delivered_to_warehouse nor available_for_pickup.
  transfer: ['转仓', '转仓', 'handoff'],
  empty_ready: ['空柜已准备', '空柜已准备', 'handoff'],
  returning_empty: ['正在还空', '正在还空', 'handoff'],
  empty_returned: ['已还空', '已还空', 'complete'],
  empty_returned_to_terminal: ['已还空至码头', '已还空至码头', 'complete'],
  completed: ['已完成', '已完成', 'complete'],
  cancelled: ['取消', '取消', 'cancelled'],
  canceled: ['取消', '取消', 'cancelled'],
  not_arrived: ['未到港', '未到港', 'neutral'],
  not_arrived_at_port: ['未到港', '未到港', 'neutral'],
  inspection: ['查验', '查验', 'neutral'],
  // 查验放行, not 查验通过 (Alan, 2026-08-12). 放行 is the word the source workbook
  // uses - the imported rows carry ocean_status_source "5查验放行" - and the word
  // the staff say. The token keeps its old spelling because three imported
  // containers are stored under it.
  inspection_cleared: ['查验放行', '查验放行', 'neutral'],
  empty_return_reported: ['已报空', '已报空', 'neutral'],
});

const TERMINAL_STATUSES = new Set(['empty_returned', 'empty_returned_to_terminal', 'completed', 'cancelled', 'canceled']);

export function normalizeContainerStatus(value) {
  const token = String(value ?? '').trim().toLowerCase()
    .replaceAll('&', ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return STATUS_ALIASES[token] || token || 'unknown';
}

function humanizeStatus(token) {
  if (!token || token === 'unknown') return 'Unknown';
  const value = token.replaceAll('_', ' ');
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function containerStatusLabel(value, locale = 'en') {
  const token = String(value ?? '').trim().toLowerCase();
  const definition = STATUS_PRESENTATION[token];
  if (!definition) return humanizeStatus(token);
  return definition[String(locale).toLowerCase().startsWith('zh') ? 1 : 0];
}

export function containerStatusPresentation(value, locale = 'en') {
  const token = normalizeContainerStatus(value);
  const definition = STATUS_PRESENTATION[token];
  return {
    token,
    label: definition ? definition[String(locale).toLowerCase().startsWith('zh') ? 1 : 0] : humanizeStatus(token),
    tone: definition?.[2] || 'neutral',
    terminal: TERMINAL_STATUSES.has(token),
  };
}

export function containerStatusClass(value, locale = 'en') {
  const status = containerStatusPresentation(value, locale);
  return `container-status container-status--${status.tone} container-status--${status.token}`;
}
